import { FC, forwardRef, KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';
import classNames from 'classnames/bind';
import { EVENT_KEY } from '../../types/enums';
import { useTranslation } from '../Translation/TranslationProvider';
import { NextgenImage } from '../NextgenImage/NextgenImage';
import { VariantSelectorPropsTypes } from './types/propType';
import styles from './VariantSelector.module.scss';

const cx = classNames.bind(styles);

const VariantSelector: FC<VariantSelectorPropsTypes> = forwardRef<HTMLDivElement, VariantSelectorPropsTypes>(
  (props: VariantSelectorPropsTypes, ref) => {
    const {
      disabled = false,
      selected = false,
      title,
      onVariantSelectorClick,
      className,
      ariaLabel,
      colorCode,
      imgSrc,
      ...restProps
    } = props;
    const t = useTranslation();

    const ariaLabelValue = ariaLabel || t('nextgen.IconButton.ariaLabel');

    const eventGroup = {
      onClick: (e: ReactKeyboardEvent | ReactMouseEvent) => onVariantSelectorClick?.(e),
      onKeyDown: (e: ReactKeyboardEvent) => {
        if (e.key === EVENT_KEY.ENTER) {
          onVariantSelectorClick?.(e);
        }
      },
    };

    const renderVariantSelector = () => {
      if (imgSrc) {
        return (
          <button
            {...eventGroup}
            aria-label={ariaLabelValue}
            disabled={disabled}
            tabIndex={0}
            className={styles['variant-selector-image-container']}
          >
            <span className={styles['variant-selector-image-content']}>
              <NextgenImage src={imgSrc} alt="Avatar" data-testid="image-variant-selector-test-id" />
            </span>
            {disabled && (
              <span className={styles['image-variant-selector-striked']} data-testid="image-strike-testid"></span>
            )}
          </button>
        );
      } else if (colorCode) {
        return (
          <button
            className={`${styles['swatch-variant-selector']} ${
              disabled ? styles['disabled-swatch-variant-selector'] : ''
            }`}
            style={{ background: `${colorCode}` }}
            disabled={disabled}
            data-testid="swatch-variant-selector-test-id"
            {...eventGroup}
          >
            {disabled && <span className={styles['swatch-variant-selector-striked']}></span>}
          </button>
        );
      } else {
        return (
          <button
            className={`${styles['variant-selector-basic']}`}
            data-testid="variant-selector-test-id"
            disabled={disabled}
            {...restProps}
            {...eventGroup}
          >
            <span className={`${styles['variant-selector-inner-content']}`}>{title}</span>
          </button>
        );
      }
    };

    const CClassname = cx(
      'variant-selector-container',
      { 'variant-selector-active': selected, 'variant-selector-disabled': disabled },
      className,
    );

    return (
      <div className={CClassname} ref={ref} aria-label={ariaLabelValue}>
        {renderVariantSelector()}
      </div>
    );
  },
);
VariantSelector.displayName = 'VariantSelector';
export default VariantSelector;
